import React, { ComponentType, lazy, LazyExoticComponent, ReactNode } from 'react';
import { Redirect } from 'react-router-dom';

import { ErrorPage } from '@applyboard/business-ui-legacy';

export interface IRoute {
  // Path
  path: string;
  // Exact path match
  exact: boolean;
  // Preloader for lazy loading
  // eslint-disable-next-line no-undef -- eslint is not finding NonNullable.
  fallback: NonNullable<ReactNode> | null;
  // Lazy Loaded component
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: LazyExoticComponent<ComponentType<any>> | ComponentType<any>;
  // Sub routes
  routes?: IRoute[];
  // Redirect path
  redirect?: string;
  // If router is private, this is going to be true
  private?: boolean;
  fullscreen?: boolean;
  layoutVariant?: 'modern';
}

export const ROUTES: IRoute[] = [
  {
    path: '/search/compare/:token',
    component: lazy(
      () =>
        import(/* webpackChunkName: "searchprogramcompare" */ '../app/pages/SearchProgramsCompare'),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/quick_search/compare/:token',
    component: ({ match }: { match: { params: { token: string } } }) => (
      <Redirect to={`/search/compare/${match.params.token}`} />
    ),
    exact: true,
    private: false,
    fallback: null,
  },
  {
    path: '/study-in/:country_name/scholarships/:scholarship_id',
    component: lazy(() => import(/* webpackChunkName: "search" */ '../app/pages/Scholarship')),
    exact: true,
    fallback: null,
    private: false,
  },
  {
    path: '/search',
    component: lazy(() => import(/* webpackChunkName: "search" */ '../app/pages/Search')),
    exact: true,
    fallback: null,
    private: false,
  },
  {
    path: '/search/help',
    component: lazy(() => import(/* webpackChunkName: "search" */ '../app/pages/SearchHelp')),
    exact: true,
    fallback: null,
    private: false,
  },
  {
    path: '/partners/:agent_id/intake-form',
    component: lazy(
      () => import(/* webpackChunkName: "studentintakeform" */ '../app/pages/StudentIntakeForm'),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/new_associate',
    component: lazy(
      () => import(/* webpackChunkName: "newassociate" */ '../app/pages/RpRegistration'),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/new_agent',
    component: () => <Redirect to="/new_associate" />,
    exact: true,
    private: false,
    fallback: null,
  },
  {
    path: '/schools/:school_name',
    component: lazy(() => import(/* webpackChunkName: "school" */ '../app/pages/School')),
    exact: true,
    private: false,
    fallback: null,
  },
  {
    path: '/recommended_programs',
    component: lazy(
      () =>
        import(/* webpackChunkName: "recommended-programs" */ '../app/pages/RecommendedPrograms'),
    ),
    exact: false,
    private: false,
    fallback: null,
  },
  {
    path: '/schools/:school_id/programs/:id',
    component: lazy(() => import(/* webpackChunkName: "program" */ '../app/pages/Program')),
    exact: true,
    private: false,
    fallback: null,
  },
  {
    path: '/flywire/:id',
    component: lazy(() => import(/* webpackChunkName: "flywire" */ '../app/pages/Flywire')),
    exact: true,
    private: false,
    fallback: null,
  },
  {
    path: '/tuition-deposit/initiate-payment',
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "initiatepayment" */
          '../app/pages/Flywire/TuitionDeposit/InitiatePayment'
        ),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/tuition-deposit/resend-email/:userId',
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "tuitiondeposit-resend-email" */ '../app/pages/Flywire/TuitionDeposit/ResendEmail'
        ),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/tuition-deposit/view-payment',
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "viewpayment" */
          '../app/pages/Flywire/TuitionDeposit/ViewPayment'
        ),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/tuition-deposit/order-status/:paymentId',
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "orderstatus" */
          '../app/pages/Flywire/TuitionDeposit/OrderStatus'
        ),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/gic/rbc/initiate',
    component: lazy(
      () => import(/* webpackChunkName: "gic" */ '../app/pages/Gic/RbcGic/RbcGicForm'),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/gic/td/initiate',
    component: lazy(() => import(/* webpackChunkName: "gic" */ '../app/pages/Gic/TdGic/TdGicForm')),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/gic/apply',
    component: lazy(
      () => import(/* webpackChunkName: "gic" */ '../app/pages/Gic/LandingPage/GicLandingPage'),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/loan/initiate',
    component: lazy(() => import('../app/pages/Loans/LoansForm')),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/loan/follow_up/:loanId',
    component: lazy(() => import('../app/pages/LoansFollowup')),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/rewards',
    component: lazy(() => import('../app/pages/Rewards/RewardsPage')),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/visa-calculator',
    component: lazy(
      () => import(/* webpackChunkName: "visacalculator" */ '../app/pages/VisaProbability'),
    ),
    exact: true,
    private: false,
    fallback: null,
  },
  {
    path: '/agency/signup',
    component: lazy(
      () => import(/* webpackChunkName: "agencysignup" */ '../app/pages/NewRpSignup'),
    ),
    exact: true,
    private: false,
    fallback: null,
    fullscreen: true,
  },
  {
    path: '/accommodations',
    component: lazy(() => import('../app/pages/Accommodations/Accommodations')),
    exact: false,
    private: false,
    fallback: null,
    layoutVariant: 'modern',
  },
  {
    path: '/unauthorized',
    component: () => <ErrorPage errorCode={401} />,
    exact: true,
    fallback: null,
  },
  {
    path: '*',
    component: () => /* webpackChunkName: "error-page" */ <ErrorPage errorCode={404} />,
    exact: false,
    fallback: null,
  },
];
